import { COMPANION_PATH } from '@/hooks/queries/companion-keys';
import api from '@/libs/axiosInstance';
import { ErrorCode, type ApiResponse, type CompanionPanelConversationDto } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

/** Base path of the conversational panel's endpoints. */
export const COMPANION_PANEL_PATH = `${COMPANION_PATH}/panel`;

/**
 * Its own namespace, not under `companionKeys`: every feed wake-up invalidates
 * the feed's keys, and that must never refetch a conversation mid-answer.
 */
export const companionPanelKeys = {
  conversation: (brandId: string) => ['companion-panel', brandId, 'conversation'] as const,
};

/** The panel is closed for this organization (kill switch or flag off), as the server names it. */
export const isCompanionPanelUnavailable = (error: unknown): boolean =>
  isAxiosError(error) &&
  error.response?.status === 404 &&
  error.response.data?.code === ErrorCode.E_COMPANION_PANEL_UNAVAILABLE;

const RUNNING_POLL_MS = 1000;

const fetchConversation = async (): Promise<CompanionPanelConversationDto | null> => {
  const { data } = await api.get<ApiResponse<CompanionPanelConversationDto | null>>(
    `${COMPANION_PANEL_PATH}/conversation`
  );
  return data.data ?? null;
};

export const lastTurnOf = (conversation: CompanionPanelConversationDto | null | undefined) =>
  conversation?.turns.at(-1) ?? null;

/**
 * The brand's current conversation with the Companion, or null when there is
 * none. The thread renders from this cache alone: a streamed answer writes into
 * it, and a turn still running with no stream attached (a reload, a dropped
 * connection) is polled every second until it settles.
 */
export const useCompanionPanelConversationQuery = (
  brandId: string,
  { pollWhileRunning = false }: { pollWhileRunning?: boolean } = {}
) =>
  useQuery<CompanionPanelConversationDto | null, Error>({
    queryKey: companionPanelKeys.conversation(brandId),
    queryFn: fetchConversation,
    enabled: !!brandId,
    retry: (failureCount, error) => !isCompanionPanelUnavailable(error) && failureCount < 2,
    // Never refreshed behind the stream's back (a remount, a focus): a refetch
    // in the middle of an answer would replace live text with what the server
    // last flushed. It is refreshed on purpose instead: after a stream, while a
    // turn runs with no stream, and when the panel opens.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (query) =>
      pollWhileRunning && lastTurnOf(query.state.data)?.status === 'running'
        ? RUNNING_POLL_MS
        : false,
  });
